/**
 * Firebase Stripe Service
 * Enhanced Stripe integration using Firebase Extension for Stripe Payments
 * Integrates with invertase/firestore-stripe-payments@0.3.11
 */

import { 
  getFirestore, 
  collection, 
  doc, 
  addDoc, 
  onSnapshot, 
  query, 
  where,
  orderBy,
  limit,
  getDocs,
  getDoc,
  Timestamp
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '@/config/firebase';

// Types for the Firebase Stripe Extension
export interface Product {
  id: string;
  name: string;
  description?: string;
  images?: string[];
  metadata?: Record<string, any>;
  active: boolean;
  role?: string;
}

export interface Price {
  id: string;
  product: string;
  unit_amount: number;
  currency: string;
  type: 'one_time' | 'recurring';
  interval?: 'day' | 'week' | 'month' | 'year';
  interval_count?: number;
  trial_period_days?: number;
  metadata?: Record<string, any>;
  active: boolean;
}

export interface Subscription {
  id: string;
  status: 'incomplete' | 'incomplete_expired' | 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid';
  price: Price;
  quantity?: number;
  cancel_at_period_end: boolean;
  current_period_start: Timestamp;
  current_period_end: Timestamp;
  created: Timestamp;
  ended_at?: Timestamp;
  trial_start?: Timestamp;
  trial_end?: Timestamp;
  metadata?: Record<string, any>;
}

export interface CheckoutSession {
  id?: string;
  url?: string;
  success_url: string;
  cancel_url: string;
  price?: string;
  quantity?: number;
  mode: 'payment' | 'subscription';
  metadata?: Record<string, any>;
  allow_promotion_codes?: boolean;
  trial_period_days?: number;
}

export interface Customer {
  id: string;
  email?: string;
  name?: string;
  phone?: string;
  address?: any;
  metadata?: Record<string, any>;
}

/**
 * Firebase Stripe Service Class
 * Uses the Firebase Extension for Stripe Payments
 */
class FirebaseStripeService {
  private auth = getAuth();
  private functions = getFunctions();
  
  // Collection names (should match extension configuration)
  private readonly PRODUCTS_COLLECTION = 'products';
  private readonly CUSTOMERS_COLLECTION = 'customers';
  private readonly CHECKOUT_SESSIONS_COLLECTION = 'checkout_sessions';
  private readonly SUBSCRIPTIONS_COLLECTION = 'subscriptions';

  /**
   * Get all active products
   */
  async getProducts(): Promise<Product[]> {
    try {
      const productsRef = collection(db, this.PRODUCTS_COLLECTION);
      const q = query(
        productsRef, 
        where('active', '==', true),
        orderBy('metadata.order', 'asc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Product));
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  }

  /**
   * Get prices for a product
   */
  async getPricesForProduct(productId: string): Promise<Price[]> {
    try {
      const pricesRef = collection(db, this.PRODUCTS_COLLECTION, productId, 'prices');
      const q = query(
        pricesRef,
        where('active', '==', true),
        orderBy('unit_amount', 'asc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        product: productId,
        ...doc.data()
      } as Price));
    } catch (error) {
      console.error('Error fetching prices:', error);
      throw error;
    }
  }

  /**
   * Create a checkout session
   */
  async createCheckoutSession(sessionData: CheckoutSession): Promise<{ url: string }> {
    try {
      const user = this.auth.currentUser;
      console.log('🔍 Creating checkout session - Auth state:', {
        user: user ? {
          uid: user.uid,
          email: user.email,
          emailVerified: user.emailVerified
        } : null,
        sessionData
      });

      if (!user) {
        throw new Error('User must be authenticated');
      }

      // Create checkout session document
      const checkoutSessionRef = collection(
        db, 
        this.CUSTOMERS_COLLECTION, 
        user.uid, 
        this.CHECKOUT_SESSIONS_COLLECTION
      );

      console.log('📝 Creating checkout session document for user:', user.uid);

      const docRef = await addDoc(checkoutSessionRef, {
        ...sessionData,
        created: Timestamp.now()
      });

      console.log('✅ Checkout session document created:', docRef.id);

      // Wait for the extension to process and add the URL
      console.log('⏳ Waiting for Firebase extension to process checkout session...');

      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          unsubscribe();
          reject(new Error('Timeout waiting for checkout session URL'));
        }, 30000); // 30 second timeout

        const unsubscribe = onSnapshot(docRef, (doc) => {
          const data = doc.data();
          console.log('📄 Checkout session document updated:', data);

          if (data?.url) {
            clearTimeout(timeout);
            unsubscribe();
            console.log('✅ Checkout session URL received:', data.url);
            resolve({ url: data.url });
          } else if (data?.error) {
            clearTimeout(timeout);
            unsubscribe();
            console.error('❌ Checkout session error:', data.error);
            reject(new Error(data.error.message));
          }
        });
      });
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  /**
   * Get current user's subscriptions
   */
  async getCurrentSubscriptions(): Promise<Subscription[]> {
    try {
      const user = this.auth.currentUser;
      if (!user) {
        throw new Error('User must be authenticated');
      }

      const subscriptionsRef = collection(
        db, 
        this.CUSTOMERS_COLLECTION, 
        user.uid, 
        this.SUBSCRIPTIONS_COLLECTION
      );
      
      const q = query(
        subscriptionsRef,
        where('status', 'in', ['trialing', 'active']),
        orderBy('created', 'desc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Subscription));
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      throw error;
    }
  }

  /**
   * Create a billing portal session
   */
  async createPortalSession(returnUrl: string): Promise<{ url: string }> {
    try {
      const user = this.auth.currentUser;
      if (!user) {
        throw new Error('User must be authenticated');
      }

      // Use the extension's cloud function
      const createPortalLink = httpsCallable(this.functions, 'ext-firestore-stripe-payments-createPortalLink');
      const result = await createPortalLink({ returnUrl });
      
      return result.data as { url: string };
    } catch (error) {
      console.error('Error creating portal session:', error);
      throw error;
    }
  }

  /**
   * Listen to subscription changes
   */
  subscribeToSubscriptions(callback: (subscriptions: Subscription[]) => void): () => void {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const subscriptionsRef = collection(
      db, 
      this.CUSTOMERS_COLLECTION, 
      user.uid, 
      this.SUBSCRIPTIONS_COLLECTION
    );

    return onSnapshot(subscriptionsRef, (snapshot) => {
      const subscriptions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Subscription));
      callback(subscriptions);
    });
  }

  /**
   * Check if user has active subscription
   */
  async hasActiveSubscription(): Promise<boolean> {
    try {
      const subscriptions = await this.getCurrentSubscriptions();
      return subscriptions.some(sub => 
        sub.status === 'active' || sub.status === 'trialing'
      );
    } catch (error) {
      console.error('Error checking subscription status:', error);
      return false;
    }
  }

  /**
   * Get user's subscription tier
   */
  async getSubscriptionTier(): Promise<'free' | 'pro' | 'elite'> {
    try {
      const subscriptions = await this.getCurrentSubscriptions();
      const activeSub = subscriptions.find(sub => 
        sub.status === 'active' || sub.status === 'trialing'
      );

      if (!activeSub) return 'free';

      // Determine tier based on price metadata or product role
      const role = activeSub.price?.metadata?.role || 'pro';
      return role as 'free' | 'pro' | 'elite';
    } catch (error) {
      console.error('Error getting subscription tier:', error);
      return 'free';
    }
  }

  /**
   * Check feature access based on subscription
   */
  async hasFeatureAccess(feature: 'ai' | 'wearables' | 'analytics' | 'adFree' | 'priority'): Promise<boolean> {
    try {
      const tier = await this.getSubscriptionTier();
      
      switch (feature) {
        case 'adFree':
        case 'ai':
        case 'wearables':
        case 'analytics':
          return ['pro', 'elite'].includes(tier);
        case 'priority':
          return tier === 'elite';
        default:
          return false;
      }
    } catch (error) {
      console.error('Error checking feature access:', error);
      return false;
    }
  }
}

export const firebaseStripeService = new FirebaseStripeService();
export default firebaseStripeService;
